

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";

// ── SEGMENTED MPAN DISPLAY ─────────────────────────────
// Matches image 8 — S prefix, top line, core line in grid
function SupplyNoDisplay({ topLine = "", core = "" }) {
  // Top line segments: 2 | 4 | 3  (total 9 digits)
  const t = topLine.replace(/\s/g, "");
  const topSeg = [t.slice(0, 2), t.slice(2, 6), t.slice(6, 9)];

  // Core segments: 2 | 4 | 4 | 3  (total 13 digits)
  const c = core.replace(/\s/g, "");
  const coreSeg = [c.slice(0, 2), c.slice(2, 6), c.slice(6, 10), c.slice(10, 13)];

  if (!topLine && !core) {
    return <span className="text-gray-400 text-sm">—</span>;
  }

  return (
    <div className="inline-flex border border-gray-400 bg-white overflow-hidden">
      {/* S prefix column */}
      <div className="flex items-center justify-center w-10 bg-white border-r border-gray-300">
        <span className="text-blue-600 font-bold text-lg">S</span>
      </div>

      {/* Segments grid */}
      <div className="flex flex-col">
        {/* TOP LINE */}
        <div className="flex border-b border-gray-300">
          {topSeg.map((seg, i) => (
            <div
              key={i}
              className={`px-3 py-1 text-sm font-mono text-gray-800 text-center
                ${i < topSeg.length - 1 ? "border-r border-gray-300" : ""}`}
              style={{ minWidth: "60px" }}
            >
              {seg || <span className="text-gray-300">—</span>}
            </div>
          ))}
        </div>

        {/* CORE LINE */}
        <div className="flex">
          {coreSeg.map((seg, i) => (
            <div
              key={i}
              className={`px-3 py-1 text-sm font-mono text-gray-400 text-center
                ${i < coreSeg.length - 1 ? "border-r border-gray-300" : ""}`}
              style={{ minWidth: "60px" }}
            >
              {seg || <span className="text-gray-200">—</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SECTION CARD ───────────────────────────────────────
function SectionCard({ title, children }) {
  return (
    <div className="mb-5">
      <div className="border border-gray-300 bg-[#dbe9f5]">
        <div className="px-6 py-3 border-b border-gray-300">
          <h3 className="text-xs font-bold uppercase text-gray-600 tracking-widest">
            {title}
          </h3>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── READ FIELD ─────────────────────────────────────────
function ReadField({ label, value, labelWidth = "w-44", mono = false }) {
  return (
    <div className="flex items-center gap-3">
      <label className={`${labelWidth} text-[12px] font-semibold text-gray-700 uppercase shrink-0`}>
        {label}
      </label>
      <div className={`flex-1 border border-gray-400 px-3 py-2 text-sm text-gray-900 bg-white min-h-[34px] ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );
}

// ── DISPOSITION BADGE ──────────────────────────────────
function DispositionBadge({ value }) {
  const color =
    value?.startsWith("Quality")  ? "bg-green-100 text-green-700 border border-green-300" :
    value?.startsWith("Validate") ? "bg-blue-100 text-blue-700 border border-blue-300" :
    value === "Callback"          ? "bg-yellow-100 text-yellow-700 border border-yellow-300" :
    value === "Reject"            ? "bg-red-100 text-red-700 border border-red-300" :
    value === "Hold"              ? "bg-gray-100 text-gray-600 border border-gray-300" :
                                    "bg-slate-100 text-slate-600 border border-slate-300";
  return value
    ? <span className={`inline-block px-3 py-1 text-xs font-bold uppercase rounded-full ${color}`}>{value}</span>
    : <span className="text-gray-400 text-sm">—</span>;
}

// ── HELPERS ────────────────────────────────────────────
function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB");
}
function formatDateTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB");
}
function getAudioMime(filename) {
  if (!filename) return "audio/mpeg";
  const ext = filename.split(".").pop().toLowerCase();
  const map = { mp3: "audio/mpeg", wav: "audio/wav", mp4: "audio/mp4", ogg: "audio/ogg", webm: "audio/webm", aac: "audio/aac", flac: "audio/flac", opus: "audio/opus", vox: "audio/vox" };
  return map[ext] || "audio/mpeg";
}
function getFileIcon(fileName = "") {   // ← NEW
  const ext = fileName.split(".").pop().toLowerCase();
  if (ext === "png") return "🖼️";
  if (ext === "pdf") return "📄";
  if (ext === "doc" || ext === "docx") return "📝";
  return "📎";
}

// ── MAIN PAGE ──────────────────────────────────────────
export default function LeadDetailsPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const user     = JSON.parse(localStorage.getItem("user"));

  const [lead,    setLead]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLead = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(`/leads/${id}`);
        setLead(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id]);

  if (loading) return (
    <div className="p-6">
      <div className="bg-white border p-10 text-center text-slate-500">Loading lead details...</div>
    </div>
  );

  if (!lead) return (
    <div className="p-6">
      <div className="bg-white border p-10 text-center text-red-500">Lead not found</div>
    </div>
  );

  const electricity = lead.electricity_details;
  const gas         = lead.gas_details;
  const review      = lead.quality_review;
  const voxFiles    = lead.vox_files || [];
  const attachments = lead.attachments || [];   // ← NEW

  const showElec = lead.validation_type === "elec" || lead.validation_type === "both";
  const showGas  = lead.validation_type === "gas"  || lead.validation_type === "both";

  const validationLabel =
    lead.validation_type === "elec" ? "Electricity Only" :
    lead.validation_type === "gas"  ? "Gas Only" :
    lead.validation_type === "both" ? "Electricity + Gas" :
    lead.validation_type || "—";

  return (
    <div className="text-slate-800">

      {/* ── HEADER ──────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
            Lead Details
          </h1>
          <nav className="text-xs text-slate-400 mt-0.5">
            <span>Leads</span><span className="mx-1">›</span>
            <span className="text-slate-600">Lead #{lead.id}</span>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full">
            {validationLabel}
          </span>
          <button
            onClick={() => navigate("/leadslist")}
            className="px-5 py-2 border border-slate-300 hover:bg-slate-100 text-sm font-semibold"
          >
            Back
          </button>
          {user?.role === "admin" && (
            <button
              onClick={() => navigate(`/leads/edit/${lead.id}`)}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
            >
              Edit Lead
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">

        {/* ── GENERAL INFORMATION ───────────────────────── */}
        <SectionCard title="General Information">
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">

            {/* Lead ID — left only */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-500 uppercase shrink-0">Lead ID</label>
              <div className="flex-1 border border-gray-400 px-5 py-1 text-sm bg-gray-100 text-gray-700">
                #{lead.id}
              </div>
            </div>
            <div />

            {/* Validate Type + Agent ID */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Validate Type</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-gray-100 text-gray-900">
                {validationLabel}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Agent ID</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-gray-100 text-gray-900">
                {lead.created_by || "—"}
              </div>
            </div>

            {/* Lead Date + Customer Name */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Lead Date</label>
              <div className="flex-1 border border-gray-400 px-4 py-2 text-sm bg-gray-100 text-gray-900">
                {formatDate(lead.lead_date)}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Customer Name</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                {lead.contact_name || "—"}
              </div>
            </div>

            {/* Business Name + Billing Name */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Business Name</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                {lead.business_name || "—"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Billing Name</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                {lead.billing_name || "—"}
              </div>
            </div>

            {/* Business Address (left tall) + Phone/MPAN stacked (right) */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                Business Address
              </label>
              <div
                className="flex-1 border border-gray-400 px-4 py-2 text-sm bg-white text-gray-900 whitespace-pre-wrap"
                style={{ minHeight: "144px" }}
              >
                {lead.business_address || "—"}
              </div>
            </div>

            {/* Right column — Phone + MPAN stacked to match address height */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase shrink-0">Phone No</label>
                <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                  {lead.business_phone || "—"}
                </div>
              </div>

              {showElec && (
                <div className="flex items-start gap-3">
                  <label className="w-36 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">MPAN</label>
                  <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 font-mono">
                    {electricity?.mpan_core || "—"}
                  </div>
                </div>
              )}
            </div>

            {/* Postal Code */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">
                Business Postal Code
              </label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                {lead.postal_code || "—"}
              </div>
            </div>
            <div />

          </div>
        </SectionCard>

        {/* ── ELECTRICITY ───────────────────────────────── */}
        {showElec && electricity && (
          <SectionCard title="Electricity">
            <div className="space-y-3">

              <ReadField label="Current Supplier *"    value={electricity.current_supplier} />
              <ReadField label="Customer Account No"   value={electricity.customer_account_no} />
              <ReadField label="Month And Year"        value={electricity.month_and_year} />
              <ReadField label="Contract End Date *"   value={formatDate(electricity.contract_end_date)} />
              <ReadField label="Payment Method"        value={electricity.payment_method} />
              <ReadField label="Meter Type"            value={electricity.meter_type} />

              {/* CURRENT RATE */}
              <div className="border-t border-cyan-500 pt-3 mt-2">
                <h3 className="text-[13px] font-bold uppercase text-gray-700 mb-3">Current Rate</h3>
                <div className="space-y-3">
                  <ReadField label="Day"             value={electricity.day_rate} />
                  <ReadField label="Night"           value={electricity.night_rate} />
                  <ReadField label="Eve/Weekends"    value={electricity.eve_weekend_rate} />
                  <ReadField label="Standing Charge" value={electricity.standing_charge} />
                  <ReadField label="EAC *"           value={electricity.eac_rate} />
                </div>
              </div>

              {/* SUPPLY NO — segmented MPAN display */}
              <div className="flex items-start gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase pt-2 shrink-0">
                  Supply No *
                </label>
                <div className="flex-1">
                  <SupplyNoDisplay
                    topLine={electricity.mpan_top_line || ""}
                    core={electricity.mpan_core || ""}
                  />
                </div>
              </div>

              {/* Average Bill Amount + Bill Type inline */}
              <div className="flex items-center gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">
                  Average Bill Amount
                </label>
                <div className="flex flex-1 gap-2">
                  <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                    {electricity.average_bill_amount || <span className="text-gray-400">—</span>}
                  </div>
                  <div className="w-28 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 text-center">
                    {electricity.bill_type || "—"}
                  </div>
                </div>
              </div>

              <ReadField label="Average Bill Range"  value={electricity.average_bill_range} />
              <ReadField label="MSN No"              value={electricity.msn_no} mono />

            </div>
          </SectionCard>
        )}

        {/* ── GAS ───────────────────────────────────────── */}
        {showGas && gas && (
          <SectionCard title="Gas">
            <div className="space-y-3">

              <ReadField label="Current Supplier *"    value={gas.current_supplier} />
              <ReadField label="Customer Account No"   value={gas.customer_account_no} />
              <ReadField label="Month And Year"        value={gas.month_and_year} />
              <ReadField label="Contract End Date *"   value={formatDate(gas.contract_end_date)} />
              <ReadField label="Payment Method"        value={gas.payment_method} />
              <ReadField label="Annual Consumption *"  value={gas.annual_consumption} />

              {/* CURRENT RATE */}
              <div className="border-t border-cyan-500 pt-3 mt-2">
                <h3 className="text-[13px] font-bold uppercase text-gray-700 mb-3">Current Rate</h3>
                <div className="space-y-3">
                  <ReadField label="Day"             value={gas.day_rate} />
                  <ReadField label="Standing Charge" value={gas.standing_charge} />
                  <ReadField label="MPR No *"        value={gas.mprn_no} mono />
                </div>
              </div>

              {/* Average Bill Amount + Billing Frequency inline */}
              <div className="flex items-center gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 uppercase shrink-0">
                  Average Bill Amount
                </label>
                <div className="flex flex-1 gap-2">
                  <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                    {gas.average_bill_amount || <span className="text-gray-400">—</span>}
                  </div>
                  <div className="w-28 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 text-center">
                    {gas.billing_frequency || "—"}
                  </div>
                </div>
              </div>

              <ReadField label="Average Bill Range"  value={gas.average_bill_range} />
              <ReadField label="MSN No"              value={gas.msn_no} mono />

            </div>
          </SectionCard>
        )}

        {/* ── QUALITY REVIEW ─────────────────────────────── */}
        <div className="border border-gray-300 bg-[#dbe9f5]">
          <div className="px-6 pt-4 pb-2 border-b border-cyan-500">
            <h3 className="text-sm font-bold uppercase text-gray-700">Quality Review</h3>
            <p className="text-xs text-gray-500 mt-1">Only the quality team can edit this section</p>
          </div>

          <div className="p-6 space-y-4">

            {/* EMAIL */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 shrink-0">Email</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 min-h-[34px]">
                {review?.email || <span className="text-gray-400">—</span>}
              </div>
            </div>

            {/* LEAD DISPOSITION */}
            <div className="flex items-center gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 shrink-0">Lead Disposition *</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white min-h-[34px]">
                <DispositionBadge value={review?.lead_disposition} />
              </div>
            </div>

            {/* CALLBACK DATE */}
            {review?.callback_date && (
              <div className="flex items-center gap-3">
                <label className="w-44 text-[12px] font-semibold text-gray-700 shrink-0">Callback Date</label>
                <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900">
                  {new Date(review.callback_date).toLocaleString("en-GB")}
                </div>
              </div>
            )}

            {/* AGENT FEEDBACK */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 pt-2 shrink-0">Agent Feedback</label>
              <div className="flex-1 border border-gray-400 px-3 py-2 text-sm bg-white text-gray-900 whitespace-pre-wrap min-h-[120px]">
                {review?.agent_feedback || <span className="text-gray-400">—</span>}
              </div>
            </div>

            {/* QUALITY FEEDBACK */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 pt-2 shrink-0">Quality Feedback</label>
              <div className={`flex-1 border px-3 py-2 text-sm whitespace-pre-wrap min-h-[120px]
                ${user?.role === "admin" || user?.role === "quality"
                  ? "border-gray-400 bg-white text-gray-900"
                  : "border-gray-300 bg-gray-100 text-gray-500"}`}>
                {review?.quality_feedback || <span className="text-gray-400">—</span>}
              </div>
            </div>

            {/* VOX FILES */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 pt-2 shrink-0">
                Upload VOX Files
              </label>
              <div className="flex-1">
                {voxFiles.length > 0 ? (
                  <div className="space-y-3">
                    {voxFiles.map((f) => (
                      <div
                        key={f.id}
                        className="border border-gray-400 bg-white px-3 py-2"
                      >
                        <audio controls preload="auto" className="w-full">
                          <source
                            src={`https://ratcrm.zentelex.com/uploads/${f.vox_file}`}
                            type={getAudioMime(f.vox_file)}
                          />
                        </audio>
                        <p className="text-[11px] text-gray-400 mt-1">
                          🎵 {f.vox_file}
                          {f.created_at && (
                            <span className="ml-2">
                              ({new Date(f.created_at).toLocaleString("en-GB")})
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-gray-400 bg-white px-3 py-2 text-sm text-gray-400">
                    No recordings uploaded
                  </div>
                )}
              </div>
            </div>

            {/* ── ATTACHMENTS (view only) ─────────────  ← NEW */}
            <div className="flex items-start gap-3">
              <label className="w-44 text-[12px] font-semibold text-gray-700 pt-2 shrink-0">
                Attachments
              </label>
              <div className="flex-1">
                {attachments.length > 0 ? (
                  <div className="space-y-2">
                    {attachments.map((a) => (
                      <a
                        key={a.id}
                        href={`https://ratcrm.zentelex.com/uploads/${a.file_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between border border-gray-400 bg-white px-3 py-2 hover:bg-blue-50 transition"
                      >
                        <span className="flex items-center gap-2 text-sm text-blue-600 hover:underline font-medium truncate pr-2">
                          {getFileIcon(a.original_name)} {a.original_name}
                        </span>
                        {a.created_at && (
                          <span className="text-[11px] text-gray-400 shrink-0">
                            {new Date(a.created_at).toLocaleString("en-GB")}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="border border-gray-400 bg-white px-3 py-2 text-sm text-gray-400">
                    No attachments uploaded
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ── FOOTER ────────────────────────────────────── */}
        <div className="border border-gray-300 bg-white px-6 py-4 flex flex-col md:flex-row justify-between gap-2 text-sm text-slate-500">
          <span>Created: {formatDateTime(lead.created_at)}</span>
          <span>Updated: {formatDateTime(lead.updated_at)}</span>
        </div>

      </div>
    </div>
  );
}

